import { Card, Grid, Icon, IconButton, Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useEffect, useState, useRef, useCallback } from "react";
import { get, postNoResponse } from "utils/WebService";
import MDSnackbar from "components/MDSnackbar";

export default function FilaSolicitacoesConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { userFunctionalities } = useUser();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Feedback Snackbar
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
    });
    const dummyFetchData = useCallback(() => {}, []);

    // Modal de Rejeição
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectingId, setRejectingId] = useState(null);
    const [motivoRejeicao, setMotivoRejeicao] = useState("");
    const [loadingAction, setLoadingAction] = useState(false);

    // Modal de Detalhes
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedSolicitacao, setSelectedSolicitacao] = useState(null);

    const loadData = () => {
        setLoading(true);
        get('/solicitacoes-suporte/pendentes').then(response => {
            setData(response?.list || response?.data || (Array.isArray(response) ? response : []));
            setLoading(false);
        }).catch(err => {
            console.error(err);
            setSbMessage(err.message || "Erro ao buscar solicitações");
            setErrorSB(true);
            setLoading(false);
        });
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleAprovar = async (id) => {
        setLoadingAction(true);
        try {
            await postNoResponse(`/solicitacoes-suporte/${id}/aprovar`, {});
            setSbMessage("Solicitação aprovada com sucesso! A devolução foi realizada.");
            setSuccessSB(true);
            setViewDialogOpen(false);
            loadData();
        } catch (err) {
            setSbMessage(err.response?.data?.message || "Erro ao aprovar solicitação");
            setErrorSB(true);
        } finally {
            setLoadingAction(false);
        }
    };

    const handleRejectClick = (id) => {
        setRejectingId(id);
        setMotivoRejeicao("");
        setRejectDialogOpen(true);
    };

    const confirmReject = async () => {
        if (!motivoRejeicao.trim()) {
            setSbMessage("Por favor, informe o motivo da rejeição.");
            setErrorSB(true);
            return;
        }
        setLoadingAction(true);
        try {
            await postNoResponse(`/solicitacoes-suporte/${rejectingId}/rejeitar`, { motivo: motivoRejeicao });
            setSbMessage("Solicitação rejeitada.");
            setSuccessSB(true);
            setRejectDialogOpen(false);
            setViewDialogOpen(false);
            loadData();
        } catch (err) {
            setSbMessage("Erro ao rejeitar solicitação");
            setErrorSB(true);
        } finally {
            setLoadingAction(false);
        }
    };

    const openViewModal = (solicitacao) => {
        setSelectedSolicitacao(solicitacao);
        setViewDialogOpen(true);
    };

    const columns = [
        {
            header: "Data Solicitação", accessorKey: "dataSolicitacao", cell: info => (
                <DefaultCell>{info.getValue()}</DefaultCell>
            )
        },
        {
            header: "Tipo", accessorKey: "tipoSolicitacao", cell: info => (
                <DefaultCell>{info.getValue() === 'DEVOLUCAO_FICHA' ? 'Ficha' : 'Contrato/Título'}</DefaultCell>
            )
        },
        {
            header: "Devedor", accessorKey: "nomeDevedor", cell: info => (
                <DefaultCell>{info.row.original.nomeDevedor ? `${info.row.original.nomeDevedor} (${info.row.original.cpfCnpjDevedor})` : "-"}</DefaultCell>
            )
        },
        {
            header: "Contrato", accessorKey: "tituloContrato", cell: info => (
                <DefaultCell>{info.getValue() || "-"}</DefaultCell>
            )
        },
        {
            header: "Solicitante", accessorKey: "nomeSolicitante", cell: info => (
                <DefaultCell>{info.getValue()}</DefaultCell>
            )
        },
        {
            header: "Status", accessorKey: "status", cell: info => (
                <MDBox display="flex" alignItems="center">
                    <MDBox width="8px" height="8px" borderRadius="50%" bgColor={info.getValue() === 'PENDENTE' ? 'warning' : 'info'} mr={1} />
                    <MDTypography variant="button" fontWeight="medium" color="text">
                        {info.getValue()}
                    </MDTypography>
                </MDBox>
            )
        },
        {
            header: "Ações", accessorKey: "acoes", disableSortBy: true, cell: info => (
                <MDBox display="flex">
                    <Tooltip title="Analisar Solicitação">
                        <IconButton onClick={() => openViewModal(info.row.original)}>
                            <MDTypography variant="h5">
                                <Icon color="info">search</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                    {userFunctionalities?.includes("c067_U") && (
                        <>
                            <Tooltip title="Aprovar e Devolver">
                                <IconButton onClick={() => handleAprovar(info.row.original.id)}>
                                    <MDTypography variant="h5">
                                        <Icon color="success">check_circle</Icon>
                                    </MDTypography>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Rejeitar Solicitação">
                                <IconButton onClick={() => handleRejectClick(info.row.original.id)}>
                                    <MDTypography variant="h5">
                                        <Icon color="error">cancel</Icon>
                                    </MDTypography>
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </MDBox>
            )
        }
    ];

    return (
        <MDBox pb={3}>
            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content={sbMessage}
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content={sbMessage}
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite
            />

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
                            <MDTypography variant="h5">Fila de Solicitações</MDTypography>
                            <MDButton variant="gradient" color="info" onClick={loadData}>
                                <Icon sx={{ fontWeight: "bold" }}>refresh</Icon>
                                &nbsp;Atualizar
                            </MDButton>
                        </MDBox>
                        
                        {loading ? (
                            <MDBox display="flex" justifyContent="center" alignItems="center" p={5}>
                                <CircularProgress color="info" />
                            </MDBox>
                        ) : (
                            <DataTable
                                loading={loading}
                                columnData={columns}
                                data={data}
                                fetchData={dummyFetchData}
                                totalPages={1}
                                totalRecords={data.length}
                                tableState={tableState}
                                showTotalEntries={false}
                                noEndBorder
                            />
                        )}
                    </Card>
                </Grid>
            </Grid>

            {/* Modal de Detalhes da Solicitação */}
            <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Detalhes da Solicitação</DialogTitle>
                <DialogContent dividers>
                    {selectedSolicitacao && (
                        <MDBox display="flex" flexDirection="column" gap={2}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold">Devedor:</MDTypography>
                                <MDTypography variant="button" display="block">{selectedSolicitacao.nomeDevedor} ({selectedSolicitacao.cpfCnpjDevedor})</MDTypography>
                            </MDBox>
                            
                            {selectedSolicitacao.tipoSolicitacao === 'DEVOLUCAO_CONTRATO' && (
                                <MDBox>
                                    <MDTypography variant="button" fontWeight="bold">Contrato:</MDTypography>
                                    <MDTypography variant="button" display="block">{selectedSolicitacao.tituloContrato}</MDTypography>
                                </MDBox>
                            )}

                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold">Observação da Solicitação:</MDTypography>
                                <MDBox mt={1} p={2} borderRadius="md" bgColor={darkMode ? "dark" : "grey-100"}>
                                    <MDTypography variant="button" style={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedSolicitacao.observacao || "Nenhuma observação informada."}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold">Histórico de Anexos:</MDTypography>
                                <MDTypography variant="caption" display="block" color="text">
                                    Para visualizar os anexos ou histórico detalhado, acesse a ficha do devedor pela Base de Dados.
                                </MDTypography>
                                <MDButton 
                                    variant="outlined" 
                                    color="info" 
                                    size="small" 
                                    sx={{ mt: 1 }}
                                    onClick={() => {
                                        sessionStorage.setItem('pendingDevedorId', selectedSolicitacao.idDevedor);
                                        window.open('/umc/base-dados', '_blank');
                                    }}
                                >
                                    Abrir Ficha do Devedor
                                </MDButton>
                            </MDBox>
                        </MDBox>
                    )}
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setViewDialogOpen(false)} color="dark">Fechar</MDButton>
                    {userFunctionalities?.includes("c067_U") && (
                        <>
                            <MDButton 
                                onClick={() => handleRejectClick(selectedSolicitacao?.id)} 
                                color="error" 
                                variant="gradient"
                                disabled={loadingAction}
                            >
                                Rejeitar
                            </MDButton>
                            <MDButton 
                                onClick={() => handleAprovar(selectedSolicitacao?.id)} 
                                color="success" 
                                variant="gradient"
                                disabled={loadingAction}
                            >
                                {loadingAction ? <CircularProgress size={24} color="inherit" /> : "Aprovar Devolução"}
                            </MDButton>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Modal de Rejeição */}
            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Rejeitar Solicitação</DialogTitle>
                <DialogContent>
                    <MDBox pt={1}>
                        <MDTypography variant="button" color="text">
                            Informe o motivo da recusa para o operador:
                        </MDTypography>
                        <MDBox mt={2}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                value={motivoRejeicao}
                                onChange={(e) => setMotivoRejeicao(e.target.value)}
                                placeholder="Motivo da rejeição..."
                            />
                        </MDBox>
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setRejectDialogOpen(false)} color="dark">
                        Cancelar
                    </MDButton>
                    <MDButton onClick={confirmReject} color="error" variant="gradient" disabled={loadingAction}>
                        {loadingAction ? <CircularProgress size={24} color="inherit" /> : "Confirmar Rejeição"}
                    </MDButton>
                </DialogActions>
            </Dialog>
        </MDBox>
    );
}
